import { memo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MenuItemButton } from "@/components/src/menu-item-button/menu-item-button.component";
import { VendorSharedFile } from "@/lib/types/job-list.types";
import { usePatientStore } from "../../store/Patient";
import { useVendorJobStore } from "../../store/VendorJobs";

interface VendorFileListItemProps {
	file: VendorSharedFile;
	vendorOrgId: string;
}

const VendorFileListItemBase: React.FC<VendorFileListItemProps> = ({
	file,
	vendorOrgId,
}) => {
	const navigate = useNavigate();
	const location = useLocation();
	const activeFileId = useVendorJobStore((state) => state.activeFileId);

	const isActiveJob = location.pathname.includes(`/job/${file.job_id}`);
	const isSelected = isActiveJob && activeFileId === file.file_id;

	const targetUrl = `/vendor/${vendorOrgId}/job/${file.job_id}?file=${file.file_id}`;

	const handleClick = useCallback(() => {
		navigate(targetUrl);
	}, [navigate, targetUrl]);

	const handleContextMenu = useCallback(
		(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
			e.preventDefault();
			window.open(`${window.location.origin}${targetUrl}`, "_blank");
		},
		[targetUrl]
	);

	const patientNameFn = usePatientStore((state) => state.patientName);
	const displayPatientName =
		file.patient_first_name || file.patient_last_name
			? patientNameFn({
					patient_id: "",
					first_name: file.patient_first_name ?? "",
					last_name: file.patient_last_name ?? "",
					code: "",
					title: "",
				})
			: "";

	const formattedDate = file.document_date
		? new Date(file.document_date).toLocaleDateString("de-CH", {
				day: "2-digit",
				month: "2-digit",
				year: "2-digit",
			})
		: "";

	return (
		<MenuItemButton
			onClick={handleClick}
			onContextMenu={handleContextMenu}
			selected={isActiveJob}
			dataTestId={`vendor-file-list-item-${file.file_id}`}
		>
			<div
				className={`flex flex-col gap-0.5 text-sm w-full ${
					isSelected ? "font-semibold" : ""
				}`}
				aria-label={`Dokument ${file.file_name} für Auftrag ${file.job_code}`}
			>
				<div className="flex justify-between items-center">
					<span className="font-bold truncate">{file.job_code}</span>
					<span className="text-xs text-gray-500 ml-2 shrink-0">
						{formattedDate}
					</span>
				</div>
				<p className="italic truncate">{file.job_title}</p>
				{displayPatientName && (
					<p className="text-xs text-gray-500 truncate">
						{displayPatientName}
					</p>
				)}
			</div>
		</MenuItemButton>
	);
};

export const VendorFileListItem = memo(VendorFileListItemBase);
